/**
 * The email-change dialog's words — Settings → Security.
 *
 * `useChangeEmail` (@tonnie/sdk) runs the change; this turns its states into
 * what the dialog says (never Clerk's English), reports once what it does not
 * recognise, and names the provider that signs in through the current
 * address — "LinkedIn keeps signing you in".
 */
import { useUser } from '@clerk/clerk-react';
import { SOCIAL_PROVIDER_NAME, socialProviderOf } from '@tonnie/foundation';
import { useAuthErrorMessage, useTranslation } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import { useChangeEmail } from '@tonnie/sdk';
import { useEffect, useMemo, useRef } from 'react';

export function useChangeEmailDialog() {
  const { t } = useTranslation();
  const describeAuthError = useAuthErrorMessage();
  const { captureException } = useErrorCapture();
  const { user } = useUser();
  const flow = useChangeEmail();
  const { state } = flow;

  const failure = useMemo(() => {
    if (state.step !== 'error') return null;
    const fallback =
      state.phase === 'send'
        ? t('profile.emailChange.sendError')
        : state.phase === 'verify'
          ? t('profile.emailChange.verifyError')
          : t('profile.emailChange.switchError');
    return describeAuthError(state.error, fallback);
  }, [state, t, describeAuthError]);

  // Report an unrecognised failure once — a refused code is not an incident.
  const reported = useRef<unknown>(null);
  useEffect(() => {
    const error =
      state.step === 'error' ? state.error : state.step === 'old_kept' ? state.error : null;
    if (!error || reported.current === error) return;
    if (state.step === 'error' && failure?.expected) return;
    reported.current = error;
    captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { feature: 'change-email', phase: state.step === 'error' ? state.phase : 'remove-old' },
    });
  }, [state, failure, captureException]);

  const linkedProvider = useMemo(() => {
    const link = user?.primaryEmailAddress?.linkedTo[0];
    const provider = link ? socialProviderOf(link.type) : null;
    return provider ? SOCIAL_PROVIDER_NAME[provider] : null;
  }, [user?.primaryEmailAddress]);

  return {
    ...flow,
    /** The translated reason for an `error` state; `null` otherwise. */
    message: failure?.message ?? null,
    linkedProvider,
  };
}
